import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;
const TOKEN_KEY = "shop_token";
const CART_KEY = "shop_cart";

// Shop requests carry the shopper token in X-Shop-Token (never the staff Authorization header).
export const shopApi = axios.create({ baseURL: API, withCredentials: true });
shopApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers["X-Shop-Token"] = t;
  delete cfg.headers.Authorization;
  return cfg;
});

export const formatZAR = (v) =>
  "R " + (Number(v) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const apiError = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return fallback;
};

const ShopContext = createContext(null);

export const ShopProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null); // null = checking, false = signed out
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  // Re-price the cart when the shopper signs in/out (their discount may change).
  useEffect(() => {
    if (customer === null) return;
    const ids = JSON.parse(localStorage.getItem(CART_KEY) || "[]").map((l) => l.product_id);
    if (!ids.length) return;
    Promise.all(ids.map((pid) => shopApi.get(`/products/${pid}`).then((r) => r.data).catch(() => null))).then((rows) => {
      const fresh = Object.fromEntries(rows.filter(Boolean).map((p) => [p.id, p]));
      setCart((prev) => prev.filter((l) => fresh[l.product_id]).map((l) => {
        const p = fresh[l.product_id];
        return { ...l, price: p.price, your_price: p.your_price, available: p.available, quantity: Math.min(l.quantity, p.available) };
      }).filter((l) => l.quantity > 0));
    });
  }, [customer]);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await shopApi.get("/auth/me");
      setCustomer(data);
    } catch {
      setCustomer(false);
    }
  }, []);

  useEffect(() => {
    shopApi.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    // Returning from Google: ShopAuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) { setCustomer(false); return; }
    if (localStorage.getItem(TOKEN_KEY)) refreshMe(); else setCustomer(false);
  }, [refreshMe]);

  const signIn = (token, c) => { localStorage.setItem(TOKEN_KEY, token); setCustomer(c); };
  const signOut = async () => {
    try { await shopApi.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setCustomer(false);
  };

  const addToCart = (p, qty = 1) => {
    setCart((prev) => {
      const found = prev.find((l) => l.product_id === p.id);
      const max = p.available ?? 9999;
      if (found) return prev.map((l) => (l.product_id === p.id ? { ...l, quantity: Math.min(max, l.quantity + qty) } : l));
      return [...prev, { product_id: p.id, name: p.name, plu_code: p.plu_code, image_url: p.image_url,
        price: p.price, your_price: p.your_price, available: max, quantity: Math.min(max, qty) }];
    });
    setCartOpen(true);
  };
  const setQty = (id, qty) => setCart((prev) => prev
    .map((l) => (l.product_id === id ? { ...l, quantity: Math.max(0, Math.min(l.available ?? 9999, qty)) } : l))
    .filter((l) => l.quantity > 0));
  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((s, l) => s + l.quantity, 0);
  const cartTotal = cart.reduce((s, l) => s + (l.your_price ?? l.price) * l.quantity, 0);

  const value = useMemo(() => ({
    customer, setCustomer, refreshMe, signIn, signOut, settings,
    cart, addToCart, setQty, clearCart, cartCount, cartTotal, cartOpen, setCartOpen,
  }), [customer, refreshMe, settings, cart, cartCount, cartTotal, cartOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

export const useShop = () => useContext(ShopContext);
